import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, BookHeart, Heart, Sparkles, Loader2, Compass } from 'lucide-react';
import LikeButton from '@/components/LikeButton';
import { toast } from 'sonner';

// Bump this to force LikeButton and grids to refetch after a toggle.
// (Small optimisation vs. a full page reload.)

const CONTENT_TYPE_LABEL = {
  bible_story: { label: 'Bible story', icon: '📖', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  ai_story: { label: 'AI story', icon: '✨', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  math_lesson: { label: 'Math lesson', icon: '🔢', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  reading_material: { label: 'Reading', icon: '📚', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  generated_material: { label: 'Materials', icon: '🎯', color: 'bg-teal-100 text-teal-700 border-teal-300' },
  worksheet: { label: 'Worksheet', icon: '📝', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  summer_slide: { label: 'Summer Slide', icon: '☀️', color: 'bg-rose-100 text-rose-700 border-rose-300' },
};

const LibraryPage = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [tab, setTab] = useState('mine');
  const [child, setChild] = useState(null);
  const [mine, setMine] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    Promise.all([
      api.get(`/children/${childId}`).catch(() => ({ data: null })),
      api.get(`/content/library/${childId}`),
      api.get('/content/recommended?limit=24'),
    ])
      .then(([c, mineRes, recRes]) => {
        if (ignore) return;
        setChild(c.data);
        setMine(mineRes.data?.items || []);
        setRecommended(recRes.data?.items || []);
      })
      .catch(() => toast.error('Could not load your library'))
      .finally(() => !ignore && setLoading(false));
    return () => { ignore = true; };
  }, [api, childId]);

  const renderCard = (item, showLikeCount = true) => {
    const cfg = CONTENT_TYPE_LABEL[item.content_type] || {
      label: item.content_type, icon: '💡', color: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    const meta = item.metadata || {};
    return (
      <Card key={`${item.content_type}-${item.content_id}`} className="hover:shadow-md transition-all border-slate-200">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{meta.thumbnail_emoji || cfg.icon}</span>
              <div>
                <p className="font-bold text-slate-800 text-sm leading-tight">
                  {meta.title || item.content_id}
                </p>
                {meta.subject && (
                  <p className="text-xs text-slate-500">{meta.subject}</p>
                )}
              </div>
            </div>
            <LikeButton
              childId={childId}
              contentType={item.content_type}
              contentId={item.content_id}
              metadata={meta}
              size="sm"
              showCount={showLikeCount}
              onToggle={({ liked: nowLiked }) => {
                if (!nowLiked) {
                  // Remove from personal library view on unlike
                  setMine((prev) => prev.filter(
                    (m) => !(m.content_type === item.content_type && m.content_id === item.content_id)
                  ));
                }
              }}
            />
          </div>
          {meta.preview && (
            <p className="text-xs text-slate-500 line-clamp-2">{meta.preview}</p>
          )}
          <div className="flex items-center gap-1 flex-wrap">
            <Badge variant="outline" className={`text-[10px] border ${cfg.color}`}>
              {cfg.icon} {cfg.label}
            </Badge>
            {item.likes !== undefined && (
              <Badge variant="outline" className="text-[10px] border-rose-200 bg-rose-50 text-rose-700">
                <Heart className="w-3 h-3 mr-0.5 fill-rose-400 text-rose-400" />
                {item.likes}
              </Badge>
            )}
            {meta.band && (
              <Badge variant="outline" className="text-[10px]">{meta.band}</Badge>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/learn/${childId}`)} data-testid="library-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <Link to={`/learn/${childId}`} className="inline-block hover:opacity-70 transition-opacity" data-testid="library-title-link">
              <h1 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
                <BookHeart className="w-5 h-5 text-rose-500" /> {child?.name ? `${child.name}'s Library` : 'Library'}
              </h1>
            </Link>
            <p className="text-xs text-slate-500">Everything you've liked — plus what other kids love.</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <div className="flex gap-2" data-testid="library-tabs">
          <Button
            variant={tab === 'mine' ? 'default' : 'outline'}
            onClick={() => setTab('mine')}
            className={tab === 'mine' ? 'bg-rose-500 hover:bg-rose-600' : ''}
            data-testid="library-tab-mine"
          >
            <Heart className="w-4 h-4 mr-1" /> My library
            {mine.length > 0 && <span className="ml-1 text-xs opacity-80">({mine.length})</span>}
          </Button>
          <Button
            variant={tab === 'recommended' ? 'default' : 'outline'}
            onClick={() => setTab('recommended')}
            className={tab === 'recommended' ? 'bg-rose-500 hover:bg-rose-600' : ''}
            data-testid="library-tab-recommended"
          >
            <Compass className="w-4 h-4 mr-1" /> Recommended by kids
            {recommended.length > 0 && <span className="ml-1 text-xs opacity-80">({recommended.length})</span>}
          </Button>
        </div>

        {loading && (
          <div className="py-24 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
          </div>
        )}

        {!loading && tab === 'mine' && (
          <>
            {mine.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center space-y-2">
                  <Sparkles className="w-8 h-8 mx-auto text-rose-400" />
                  <p className="font-semibold text-slate-700">Your library is empty</p>
                  <p className="text-sm text-slate-500">
                    Tap the heart next to any story, lesson, or worksheet you love — it&apos;ll show up here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-3" data-testid="library-mine-grid">
                {mine.map((m) => renderCard(m, false))}
              </div>
            )}
          </>
        )}

        {!loading && tab === 'recommended' && (
          <>
            {recommended.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center space-y-2">
                  <Compass className="w-8 h-8 mx-auto text-rose-400" />
                  <p className="font-semibold text-slate-700">No community picks yet</p>
                  <p className="text-sm text-slate-500">Be the first — like something to help other kids discover it!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-3" data-testid="library-recommended-grid">
                {recommended.map((m) => renderCard(m, true))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default LibraryPage;
